// src/components/SessionGate/AcceptanceFailure.tsx
// ─────────────────────────────────────────────────────────────────────────
// The blocking failure screen shown when a Firebase account exists but the
// server could NOT record (or confirm) the user's legal acceptance. The user
// does NOT enter the app from here — the server acceptance record is the legal
// gate. Shared by SignupPage (post-auth write failure) and SessionGate
// (re-acceptance write failure / status-check failure).
//
// Button order is deliberate:
//   Primary   → Retry       (most failures are transient)
//   Secondary → Report issue (for persistent failures)
//   Tertiary  → Sign out     (clean exit rather than lingering authed-but-ungated)
//
// "Report issue" must NOT depend on the same backend that just failed — if the
// API/DB is down, an in-app POST to report it would fail too. So it opens a
// mailto: with a diagnostic block prefilled. That block carries ONLY a strict
// allowlist and never any sensitive data (no passwords, no financial profile,
// no tokens, no localStorage dump).
// ─────────────────────────────────────────────────────────────────────────

import React from 'react';
import { auth } from '../../lib/firebase';
import styles from './AcceptanceFailure.module.scss';

const SUPPORT_EMAIL = 'victor@nisk.co';

// Matches the pattern used in localExport.ts — the build injects VITE_APP_VERSION;
// 'dev' locally. Read at module scope so it's stable across renders.
const APP_VERSION: string = import.meta.env.VITE_APP_VERSION ?? 'dev';

export interface AcceptanceFailureProps {
	/** How the user authenticated, for the diagnostic report. */
	provider: 'password' | 'google' | null;
	/** The error surfaced by the failed acceptance write/check. */
	errorMessage: string;
	/** The route the failure happened on (e.g. "/signup", "/reaccept"). */
	route: string;
	/** Disables the action buttons while a retry is in flight. */
	busy: boolean;
	onRetry: () => void | Promise<void>;
	onSignOut: () => void | Promise<void>;
	/** Optional server trace id (from a 500 ProblemDetails), if one was captured. */
	traceId?: string | null;
}

/**
 * Build the diagnostic block for the support email. STRICT ALLOWLIST — the only
 * fields permitted are those that carry no sensitive user data. Do not add
 * income, expenses, holdings, tokens, passwords, or a localStorage dump here.
 */
function buildReport(props: AcceptanceFailureProps): string {
	const user = auth.currentUser;
	const lines = [
		'Nisk acceptance issue report',
		'----------------------------',
		`uid: ${user?.uid ?? '(no session)'}`,
		`provider: ${props.provider ?? 'unknown'}`,
		`route: ${props.route}`,
		`appVersion: ${APP_VERSION}`,
		`timestamp: ${new Date().toISOString()}`,
		`traceId: ${props.traceId ?? '(none)'}`,
		`error: ${props.errorMessage}`,
		`userAgent: ${
			typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'
		}`,
	];
	return lines.join('\n');
}

export const AcceptanceFailure: React.FC<AcceptanceFailureProps> = (props) => {
	const { busy, onRetry, onSignOut } = props;

	const handleReport = (): void => {
		const subject = encodeURIComponent('Nisk acceptance issue');
		const body = encodeURIComponent(buildReport(props));
		// mailto works with no backend — deliberately independent of the API
		// that just failed.
		window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
	};

	return (
		<div
			className={styles.failure}
			role='alert'
			aria-live='assertive'>
			<h2 className={styles.title}>We couldn’t save your acceptance.</h2>
			<p className={styles.subtitle}>
				Your account was created, but Nisk needs to save your age
				confirmation and legal acceptance before continuing.
			</p>
			<p className={styles.subtitle}>
				Please try again. If this keeps happening, report the issue and
				we’ll look into it.
			</p>

			<div className={styles.actions}>
				<button
					className={styles.primaryBtn}
					onClick={() => void onRetry()}
					disabled={busy}>
					{busy ? 'Retrying…' : 'Retry'}
				</button>
				<button
					className={styles.secondaryBtn}
					onClick={handleReport}
					disabled={busy}>
					Report issue
				</button>
				<button
					className={styles.tertiaryBtn}
					onClick={() => void onSignOut()}
					disabled={busy}>
					Sign out
				</button>
			</div>
		</div>
	);
};

export default AcceptanceFailure;
